import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, Building2, ArrowRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { subscriptionService } from '../lib/subscriptionService';

export function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');

  const plans = [
    {
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Perfect for getting started with job search',
      cta: 'Sign Up Free',
      ctaLink: '/signup',
      popular: false,
      icon: Sparkles,
      color: 'cyan',
      features: [
        'Swipe to Apply',
        'Text-based job search',
        'Basic resume builder',
        'Messaging (limited)',
        'Standard job matches',
        'Progress tracking',
        'Community support',
      ],
    },
    {
      name: 'ResuSwipe+',
      basePrice: 8.99,
      price: subscriptionService.calculatePrice(8.99, billingCycle),
      period: billingCycle === 'weekly' ? 'week' : billingCycle === 'monthly' ? 'month' : 'year',
      description: 'Let your career run on autopilot',
      cta: 'Get ResuSwipe+',
      ctaLink: '/subscribe/plus',
      popular: true,
      icon: Zap,
      color: 'orange',
      features: [
        'Everything in Free, plus:',
        'AI Auto-Apply (10-30 jobs/day)',
        'Priority job ranking',
        'Unlimited resume revisions',
        'Unlimited interview practice',
        'Priority messaging',
        'Custom cover letters',
        'Early access to new jobs',
        'Invite-only job pools',
        'Rewards booster (2x credits)',
      ],
    },
    {
      name: 'Employer Suite',
      basePrice: 399,
      price: subscriptionService.calculatePrice(399, billingCycle),
      period: billingCycle === 'weekly' ? 'week' : billingCycle === 'monthly' ? 'month' : 'year',
      description: 'AI-powered recruiting for your entire team',
      cta: 'Schedule Demo',
      ctaLink: '/employer/demo',
      popular: false,
      icon: Building2,
      color: 'blue',
      features: [
        'Unlimited job posts',
        'AI candidate summaries',
        'Team access & collaboration',
        'Slack + Outlook integration',
        'Monday.com sync',
        'Top-tier visibility',
        'Application prediction scoring',
        'Priority support',
        'Custom branding',
        'Advanced analytics',
      ],
    },
  ];

  const addons = [
    {
      name: 'Interview FastPass',
      price: 9.99,
      period: 'month',
      description: 'Nail every interview with 24/7 AI coaching',
      features: [
        '24/7 interview practice',
        'Behavioral question library',
        'Personalized answer frameworks',
        'Real-time feedback & tone coaching',
        'Interview follow-up templates',
      ],
    },
    {
      name: 'Instant Resume Glow-Up',
      price: 29,
      period: 'one-time',
      description: 'Professional resume transformation in 48 hours',
      features: [
        'AI optimized resume',
        'ATS-boosted formatting',
        'Skill re-mapping',
        'Strength-focused rewrites',
        'PDF + DOCX export',
      ],
    },
    {
      name: 'Verified Badge',
      price: 4.99,
      period: 'one-time',
      description: 'Stand out with professional verification',
      features: [
        'Identity verification',
        'Skill proof certificate',
        'Job-readiness badge',
        'Profile highlight',
        '3x more interview requests',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 px-4 py-2 rounded-full mb-6">
            <Crown className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-semibold text-orange-900">Simple, transparent pricing</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Start free, upgrade when you're ready. No hidden fees. Cancel anytime.
          </p>

          <div className="inline-flex items-center gap-2 bg-white rounded-full p-1 shadow-md">
            <button
              onClick={() => setBillingCycle('weekly')}
              className={`px-6 py-2.5 rounded-full font-semibold transition-all ${
                billingCycle === 'weekly'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-full font-semibold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2.5 rounded-full font-semibold transition-all relative ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yearly
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                Save 16%
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isPopular = plan.popular;

            return (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl p-8 shadow-lg border-2 transition-all hover:shadow-xl ${
                  isPopular
                    ? 'border-orange-500 scale-105'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${
                      plan.color === 'orange'
                        ? 'from-orange-500 to-amber-500'
                        : plan.color === 'blue'
                        ? 'from-blue-500 to-cyan-500'
                        : 'from-cyan-500 to-teal-500'
                    } rounded-xl flex items-center justify-center`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                </div>

                <p className="text-gray-600 mb-6 min-h-[48px]">{plan.description}</p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                    {plan.period !== 'forever' && (
                      <span className="text-gray-600">/{plan.period}</span>
                    )}
                  </div>
                </div>

                <Link
                  to={plan.ctaLink}
                  className={`block w-full py-3 px-6 rounded-xl font-bold text-center transition-all mb-8 ${
                    isPopular
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </Link>

                <div className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      {feature.includes('Everything') ? (
                        <div className="text-sm font-semibold text-gray-900 py-1">
                          {feature}
                        </div>
                      ) : (
                        <>
                          <Check
                            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                              plan.color === 'orange'
                                ? 'text-orange-500'
                                : plan.color === 'blue'
                                ? 'text-blue-500'
                                : 'text-cyan-500'
                            }`}
                          />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Boost Your Success
            </h2>
            <p className="text-xl text-gray-600">
              Add these power-ups to any plan
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {addons.map((addon) => (
              <div
                key={addon.name}
                className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg"
              >
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-3xl font-bold text-gray-900">
                    ${addon.price}
                  </span>
                  <span className="text-gray-600 text-sm">{addon.period}</span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {addon.name}
                </h3>
                <p className="text-gray-600 mb-4 text-sm">{addon.description}</p>

                <div className="space-y-2 mb-6">
                  {addon.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-cyan-500" />
                      <span className="text-xs text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <button className="w-full py-2.5 px-4 bg-white border-2 border-gray-300 text-gray-900 rounded-lg font-semibold hover:border-cyan-500 hover:text-cyan-600 transition-all">
                  Add to Plan
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-12 text-center text-white">
          <Crown className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-bold mb-4">
            Enterprise & Partnerships
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Custom solutions for colleges, workforce centers, and large employers.
            Bulk licensing available starting at $2/user/month.
          </p>
          <Link
            to="/partnerships"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-cyan-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
          >
            Learn More
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            Questions about pricing? We're here to help.
          </p>
          <div className="flex items-center justify-center gap-6">
            <a
              href="#"
              className="text-cyan-600 font-semibold hover:text-cyan-700 transition"
            >
              Contact Sales
            </a>
            <span className="text-gray-400">•</span>
            <a
              href="#"
              className="text-cyan-600 font-semibold hover:text-cyan-700 transition"
            >
              View FAQ
            </a>
            <span className="text-gray-400">•</span>
            <a
              href="#"
              className="text-cyan-600 font-semibold hover:text-cyan-700 transition"
            >
              Compare Plans
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
